import TextField from '@mui/material/TextField';
import './TextInputField.css';
import { useState } from 'react';
import { InputAdornment } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface Props {
  value?: string;
  label?: string;
  placeholder?: string;
  leftComponent?: React.ReactNode;
  error?: boolean;
  type?: React.HTMLInputTypeAttribute;
  disableBorderHighlight?: boolean;
  autoFocus?: boolean;
  showClear?: boolean;
  /** Ref to the underlying <input> element, e.g. to re-assert focus programmatically. */
  inputRef?: React.Ref<HTMLInputElement>;
  onChange: (text: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

const TextInputField = ({
  value,
  label,
  placeholder,
  leftComponent,
  error,
  type = 'text',
  autoFocus,
  showClear = true,
  inputRef,
  onChange,
  onKeyDown,
}: Props) => {
  const [forceTextType, setForceTextType] = useState(false);
  const finalType = forceTextType ? 'text' : type;
  const visibilityIcon = finalType === 'password' ? 'eye' : 'eye-slash';
  const onVisibilityChange = () => setForceTextType(!forceTextType);
  const hasClearable = showClear && type !== 'password' && value && value.length > 0;
  const endAdornment =
    type === 'password' ? (
      <InputAdornment position="end">
        <div
          className="text-input-visibility-icon-wrapper"
          onClick={onVisibilityChange}
        >
          <FontAwesomeIcon icon={visibilityIcon} color="white" />
        </div>
      </InputAdornment>
    ) : hasClearable ? (
      <InputAdornment position="end">
        <div
          className="text-input-clear-wrapper"
          onClick={() => onChange('')}
        >
          <FontAwesomeIcon icon="times" color="white" size="xs" />
        </div>
      </InputAdornment>
    ) : undefined;
  const slotProps = {
    input: {
      startAdornment: leftComponent ? (
        <div className="text-input-left-component-wrapper">{leftComponent}</div>
      ) : undefined,
      endAdornment,
    },
  };

  return (
    <TextField
      label={label}
      placeholder={placeholder}
      fullWidth
      type={finalType}
      className="text-input-field"
      sx={({ colors }) => ({
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            borderColor: colors.grey,
          },
          '&.Mui-focused fieldset': {
            borderColor: colors.grey,
          },
        },
      })}
      variant="outlined"
      slotProps={slotProps}
      value={value}
      error={error}
      autoFocus={autoFocus}
      inputRef={inputRef}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
    />
  );
};

export default TextInputField;
